import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, CheckCheck, X, Phone, Calendar, RefreshCw } from 'lucide-react';
import { api } from '@/api';

const STATUS = {
  pending:   { label: 'PENDING',   cls: 'border-yellow-400/50 text-yellow-400' },
  confirmed: { label: 'CONFIRMED', cls: 'mi-tag-red' },
  completed: { label: 'DONE',      cls: 'border-white/30 text-white/70' },
  canceled:  { label: 'CANCELED',  cls: 'border-white/15 text-white/35' },
};

const fmtWhen = (iso, tz) => {
  try { return new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz || 'America/Chicago' }); }
  catch { return iso; }
};

// Phone-first bookings list: one card per appointment, one-tap Confirm / Done / Cancel.
export default function MobileBookingsList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('upcoming');
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/admin/schedule/appointments'); setItems(data.items || []); }
    catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (b, status) => {
    setBusy(b.id + status);
    const prev = items;
    setItems((cur) => cur.map((x) => (x.id === b.id ? { ...x, status } : x)));
    try { await api.patch(`/admin/schedule/appointments/${b.id}`, { status }); toast.success(`${b.name.split(' ')[0]} · ${STATUS[status].label.toLowerCase()}`); }
    catch { setItems(prev); toast.error('Could not update'); }
    finally { setBusy(null); }
  };

  const now = Date.now();
  const shown = items
    .filter((b) => (filter === 'upcoming' ? new Date(b.start_time).getTime() >= now - 3600e3 && b.status !== 'canceled' : true))
    .sort((a, b) => (filter === 'upcoming' ? new Date(a.start_time) - new Date(b.start_time) : new Date(b.start_time) - new Date(a.start_time)));

  return (
    <div className="px-4 pt-4 space-y-3" data-testid="mobile-bookings">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex border mi-line-strong">
          {['upcoming', 'all'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`mobile-bookings-filter-${f}`}
                    className={`px-3 py-1.5 text-[11px] mi-headline uppercase ${filter === f ? 'bg-[#E63946] text-white' : 'text-white/60'}`}>{f}</button>
          ))}
        </div>
        <button onClick={load} className="p-2 text-white/50" aria-label="Reload" data-testid="mobile-bookings-reload"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {loading ? <div className="text-white/50 text-sm py-10 text-center">Loading…</div>
        : !shown.length ? <div className="text-white/50 text-sm py-10 text-center" data-testid="mobile-bookings-empty">No {filter === 'upcoming' ? 'upcoming ' : ''}bookings.</div>
        : shown.map((b) => {
          const st = STATUS[b.status] || STATUS.pending;
          const vehicle = [b.vehicle_year, b.vehicle_make, b.vehicle_model].filter(Boolean).join(' ');
          const act = (status) => busy === b.id + status;
          return (
            <div key={b.id} className={`border mi-line-strong bg-[#0A0A0A] p-4 ${b.status === 'canceled' ? 'opacity-50' : ''}`} data-testid={`mobile-booking-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[#E63946] text-sm">{fmtWhen(b.start_time, b.timezone)}</div>
                  <div className="text-white mi-headline text-xl leading-tight truncate mt-0.5">{b.name}</div>
                  <div className="text-white/70 text-xs mt-0.5">{b.service_type}{vehicle ? ` · ${vehicle}` : ''}</div>
                </div>
                <span className={`mi-tag text-[8px] whitespace-nowrap ${st.cls}`} data-testid={`mobile-booking-status-${b.id}`}>{st.label}</span>
              </div>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-white/50">
                <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-white"><Phone className="w-3 h-3" /> {b.phone}</a>
                {b.gcal_event_link && <a href={b.gcal_event_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white"><Calendar className="w-3 h-3" /> GCal</a>}
                {b.notes && <span className="truncate">· {b.notes}</span>}
              </div>
              {b.status !== 'canceled' && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <button onClick={() => setStatus(b, 'confirmed')} disabled={b.status !== 'pending' || !!busy} data-testid={`mobile-booking-confirm-${b.id}`}
                          className="mi-btn-red text-[11px] py-2 justify-center disabled:opacity-30"><Check className="w-3.5 h-3.5" /> {act('confirmed') ? '…' : 'Confirm'}</button>
                  <button onClick={() => setStatus(b, 'completed')} disabled={b.status === 'completed' || !!busy} data-testid={`mobile-booking-done-${b.id}`}
                          className="mi-btn-ghost text-[11px] py-2 justify-center disabled:opacity-30"><CheckCheck className="w-3.5 h-3.5" /> {act('completed') ? '…' : 'Done'}</button>
                  <button onClick={() => { if (window.confirm(`Cancel ${b.name}'s booking?`)) setStatus(b, 'canceled'); }} disabled={!!busy} data-testid={`mobile-booking-cancel-${b.id}`}
                          className="mi-btn-ghost text-[11px] py-2 justify-center text-white/60 disabled:opacity-30"><X className="w-3.5 h-3.5" /> Cancel</button>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}
